import React, { createContext, useContext, useState, useCallback } from "react";

export interface MatchSourceAttachment {
  submissionId: string;
  attachmentId: string;
  filename: string;
  contentType: string;
  presignedUrl: string;
  thumbnailUrl?: string | null;
}

export interface MatchTargetAttachment {
  submissionId: string;
  attachmentId: string;
  filename: string;
  contentType: string;
  presignedUrl: string;
  thumbnailUrl?: string | null;
}

export interface ImageMatchState {
  /** Whether a match process is currently in progress */
  isMatchInProgress: boolean;
  /** The source attachment that started the match */
  sourceAttachment: MatchSourceAttachment | null;
  /** Attachments selected to be matched against the source */
  targetAttachments: MatchTargetAttachment[];
  /** Incremented on match completion to signal indicators to refetch */
  matchVersion: number;
  /** Start a new match with the given source attachment */
  startMatch: (source: MatchSourceAttachment) => void;
  /** Add a target attachment to the match */
  addTarget: (target: MatchTargetAttachment) => void;
  /** Remove a target attachment from the match */
  removeTarget: (attachmentId: string) => void;
  /** Cancel the current match process */
  cancelMatch: () => void;
  /** Clear everything after completing a match */
  completeMatch: () => void;
  /** Signal indicators to refetch without altering an in-progress match */
  notifyMatchChange: () => void;
  /** Check if an attachment is already selected as a target */
  isTargetSelected: (attachmentId: string) => boolean;
  /** Check if an attachment is the source */
  isSource: (attachmentId: string) => boolean;
  /** Navigate to a report in the main view (set by the list/drawer host) */
  navigateToReport: ((submissionId: string) => void) | null;
  /** Register a navigateToReport handler */
  setNavigateToReport: (
    handler: ((submissionId: string) => void) | null,
  ) => void;
}

const ImageMatchContext = createContext<ImageMatchState>({
  isMatchInProgress: false,
  sourceAttachment: null,
  targetAttachments: [],
  matchVersion: 0,
  startMatch: () => {},
  addTarget: () => {},
  removeTarget: () => {},
  cancelMatch: () => {},
  completeMatch: () => {},
  notifyMatchChange: () => {},
  isTargetSelected: () => false,
  isSource: () => false,
  navigateToReport: null,
  setNavigateToReport: () => {},
});

export const useImageMatch = () => useContext(ImageMatchContext);

export const ImageMatchProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [sourceAttachment, setSourceAttachment] =
    useState<MatchSourceAttachment | null>(null);
  const [targetAttachments, setTargetAttachments] = useState<
    MatchTargetAttachment[]
  >([]);
  const [matchVersion, setMatchVersion] = useState(0);
  const [navigateToReport, setNavigateToReportState] = useState<
    ((submissionId: string) => void) | null
  >(null);

  const setNavigateToReport = useCallback(
    (handler: ((submissionId: string) => void) | null) => {
      setNavigateToReportState(() => handler);
    },
    [],
  );

  const isMatchInProgress = sourceAttachment !== null;

  const startMatch = useCallback((source: MatchSourceAttachment) => {
    setSourceAttachment(source);
    setTargetAttachments([]);
  }, []);

  const addTarget = useCallback(
    (target: MatchTargetAttachment) => {
      if (
        sourceAttachment &&
        target.attachmentId === sourceAttachment.attachmentId
      ) {
        return; // Can't match against self
      }
      setTargetAttachments((prev) => {
        if (prev.some((t) => t.attachmentId === target.attachmentId)) {
          return prev; // Already added
        }
        return [...prev, target];
      });
    },
    [sourceAttachment],
  );

  const removeTarget = useCallback((attachmentId: string) => {
    setTargetAttachments((prev) =>
      prev.filter((t) => t.attachmentId !== attachmentId),
    );
  }, []);

  const cancelMatch = useCallback(() => {
    setSourceAttachment(null);
    setTargetAttachments([]);
  }, []);

  const completeMatch = useCallback(() => {
    setSourceAttachment(null);
    setTargetAttachments([]);
    setMatchVersion((v) => v + 1);
  }, []);

  const notifyMatchChange = useCallback(() => {
    setMatchVersion((v) => v + 1);
  }, []);

  const isTargetSelected = useCallback(
    (attachmentId: string) => {
      if (!attachmentId) return false;
      return targetAttachments.some((t) => t.attachmentId === attachmentId);
    },
    [targetAttachments],
  );

  const isSource = useCallback(
    (attachmentId: string) => {
      if (!sourceAttachment || !attachmentId) return false;
      return sourceAttachment.attachmentId === attachmentId;
    },
    [sourceAttachment],
  );

  return (
    <ImageMatchContext.Provider
      value={{
        isMatchInProgress,
        sourceAttachment,
        targetAttachments,
        matchVersion,
        startMatch,
        addTarget,
        removeTarget,
        cancelMatch,
        completeMatch,
        notifyMatchChange,
        isTargetSelected,
        isSource,
        navigateToReport,
        setNavigateToReport,
      }}
    >
      {children}
    </ImageMatchContext.Provider>
  );
};

export default ImageMatchProvider;
